import "../tos/TermsOfService.css";
import { Link } from "react-router-dom";
import icon from "../../assets/icon128.png";

const EFFECTIVE_DATE = "March 23, 2026";

export default function PrivacyPolicy() {
  document.title = "Profile Connector";
  return (
    <div className="tos-root">
      {/* Nav */}
      <nav className="tos-nav">
        <Link to="/landing" className="tos-nav-brand">
          <img src={icon} alt="Profile Connector" className="tos-nav-logo" />
          Profile Connector
        </Link>
        <Link to="/landing" className="tos-nav-back">
          ← Back to Home
        </Link>
      </nav>

      {/* Hero */}
      <div className="tos-hero">
        <div className="tos-hero-badge">🔒 Privacy</div>
        <h1>Privacy Policy</h1>
        <p className="tos-hero-meta">
          Effective date: {EFFECTIVE_DATE} &nbsp;·&nbsp; Last updated: {EFFECTIVE_DATE}
        </p>
      </div>

      <div className="tos-layout">
        {/* Table of Contents */}
        <aside className="tos-toc">
          <div className="tos-toc-title">On this page</div>
          <ul>
            <li><a href="#overview">1. Overview</a></li>
            <li><a href="#data-collected">2. Data We Collect</a></li>
            <li><a href="#data-not-collected">3. Data We Don't Collect</a></li>
            <li><a href="#how-we-use">4. How We Use Data</a></li>
            <li><a href="#data-sharing">5. Data Sharing & Human Review</a></li>
            <li><a href="#data-retention">6. Data Retention</a></li>
            <li><a href="#security">7. Security</a></li>
            <li><a href="#cookies">8. Cookies & Local Storage</a></li>
            <li><a href="#third-party">9. Third-Party Platforms</a></li>
            <li><a href="#your-rights">10. Your Rights</a></li>
            <li><a href="#children">11. Children's Privacy</a></li>
            <li><a href="#changes">12. Changes to This Policy</a></li>
            <li><a href="#contact">13. Contact Us</a></li>
          </ul>
        </aside>

        {/* Content */}
        <main className="tos-content">
          <div className="tos-highlight">
            <p>
              Your privacy is important to us. This Privacy Policy explains what
              data Profile Connector collects, how it is used, and your rights
              regarding that data. Profile Connector is designed with a
              privacy-first approach — we collect as little data as possible to
              provide the Service.
            </p>
          </div>

          <section className="tos-section" id="overview">
            <h2>1. Overview</h2>
            <p>
              Profile Connector ("we," "us," or "our") operates the Profile
              Connector Chrome browser extension and associated web frontend (the
              "Service"). This Privacy Policy applies to all users of the Service
              and describes how we handle personal information.
            </p>
            <p>
              The extension has a single purpose: to allow you to securely
              connect your existing LinkedIn account to a compatible third-party
              partner platform. All data access is strictly limited to what is
              necessary to fulfill that purpose.
            </p>
            <p>
              Profile Connector's use of data complies with the{" "}
              <strong>Chrome Web Store User Data Policy</strong>, including its
              Limited Use requirements. Data accessed by this extension is used
              only to provide or improve the extension's stated single purpose,
              and is not used for any secondary purpose.
            </p>
            <p>
              Profile Connector is not affiliated with, endorsed by, or sponsored
              by LinkedIn Corporation or its parent company Microsoft.
            </p>
          </section>

          <section className="tos-section" id="data-collected">
            <h2>2. Data We Collect</h2>

            <h3>2.1 LinkedIn Session Token (Personal & Sensitive Data)</h3>
            <p>
              When you initiate a connection using the Chrome extension, we
              access the LinkedIn session token stored in your browser's cookie
              store for the <code>linkedin.com</code> domain. Per the Chrome Web
              Store User Data Policy, data stored in a website's browser
              cookie store is classified as <strong>personal and sensitive user
              data</strong> and is handled accordingly.
            </p>
            <p>
              This token is transmitted directly to the partner platform you are
              connecting to over a TLS 1.3 encrypted connection. The session
              token is <strong>not stored</strong> by Profile Connector beyond
              the instant of transmission. We do not log, cache, or retain it
              on our servers.
            </p>

            <h3>2.2 Anonymized Usage Statistics</h3>
            <p>
              The Service may collect anonymized, non-identifying usage events
              such as whether a connection attempt succeeded or failed. These
              events contain no personal information and cannot be linked back
              to any individual user.
            </p>

            <h3>2.3 Error & Crash Reports</h3>
            <p>
              We use Sentry to capture application errors. Error reports may
              include technical information such as browser version, operating
              system, and a stack trace. They do not include LinkedIn session
              tokens or any personally identifiable information.
            </p>
          </section>

          <section className="tos-section" id="data-not-collected">
            <h2>3. Data We Don't Collect</h2>
            <p>
              We want to be explicit about what we do <strong>not</strong> collect:
            </p>
            <ul>
              <li>Your LinkedIn email address or password.</li>
              <li>
                Your LinkedIn profile information — name, headline, photo,
                connections, messages, posts, or any page content.
              </li>
              <li>Your browsing history or activity on any website.</li>
              <li>IP addresses linked to individual users.</li>
              <li>Any data from websites other than LinkedIn.</li>
              <li>Device identifiers or hardware information.</li>
            </ul>
          </section>

          <section className="tos-section" id="how-we-use">
            <h2>4. How We Use Data</h2>
            <p>The limited data we access is used solely to:</p>
            <ul>
              <li>
                Facilitate the secure connection of your LinkedIn account to the
                partner platform you have chosen.
              </li>
              <li>
                Monitor aggregate reliability and error rates to improve the
                Service.
              </li>
              <li>
                Diagnose and fix technical issues via anonymized error reports.
              </li>
            </ul>
            <p>
              We do not use any data for advertising, profiling, or marketing
              purposes. We do not transfer any user data to third parties for
              personalized advertising, retargeted advertising, or interest-based
              advertising. We do not use data to assess creditworthiness or for
              any purpose beyond operating and improving this extension.
            </p>
          </section>

          <section className="tos-section" id="data-sharing">
            <h2>5. Data Sharing</h2>

            <h3>5.1 Partner Platforms</h3>
            <p>
              Your LinkedIn session token is transmitted to the specific partner
              platform you are actively connecting to. No other party receives
              your session token.
            </p>

            <h3>5.2 Service Providers</h3>
            <p>
              We use Sentry (sentry.io) as an error-monitoring service provider.
              Sentry receives anonymized crash and error data as described in
              Section 2.3. This data contains no LinkedIn session tokens or
              personally identifiable information. Sentry is contractually
              prohibited from using this data for any purpose other than
              providing the error-monitoring service.
            </p>

            <h3>5.3 No Human Review of User Data</h3>
            <p>
              No Profile Connector employee or contractor reads or reviews
              personal user data, including LinkedIn session tokens. The only
              exceptions are: (a) with your explicit consent; (b) for security
              or fraud investigations; or (c) where required by applicable law.
              Anonymized, aggregated usage statistics that cannot identify any
              individual may be reviewed for product improvement purposes.
            </p>

            <h3>5.4 Legal Obligations</h3>
            <p>
              We may disclose information if required to do so by law or in
              response to valid legal process (such as a court order or
              government request), provided that we are legally permitted to
              notify you.
            </p>

            <h3>5.5 No Sale of Data</h3>
            <p>
              We do not sell, rent, lease, or otherwise disclose personal data
              to any third party for commercial purposes.
            </p>
          </section>

          <section className="tos-section" id="data-retention">
            <h2>6. Data Retention</h2>
            <p>
              Profile Connector does not persistently store your LinkedIn session
              token. It is read from your browser, transmitted to the partner
              platform, and immediately discarded — no copy is held on our
              servers.
            </p>
            <p>
              Anonymized usage statistics and error logs are retained for up to
              90 days for reliability analysis, after which they are
              automatically deleted. These records contain no personal
              information.
            </p>
          </section>

          <section className="tos-section" id="security">
            <h2>7. Security</h2>
            <p>
              All data transmitted by the Service is protected by TLS 1.3
              encryption. The Chrome extension requests only the minimum
              permissions necessary (<code>activeTab</code> and{" "}
              <code>cookies</code> scoped to <code>linkedin.com</code>) to
              perform the connection.
            </p>
            <p>
              While we take reasonable technical and organizational measures to
              protect data in transit, no method of transmission over the
              internet is completely secure. We encourage you to keep your
              browser and operating system up to date.
            </p>
          </section>

          <section className="tos-section" id="cookies">
            <h2>8. Cookies & Local Storage</h2>
            <p>
              Profile Connector itself does not set cookies or write to local
              storage in your browser. The extension reads LinkedIn's own
              session cookie solely to perform the account connection you
              initiated.
            </p>
            <p>
              The web frontend (this site) does not use tracking cookies,
              advertising cookies, or persistent analytics identifiers.
            </p>
          </section>

          <section className="tos-section" id="third-party">
            <h2>9. Third-Party Platforms</h2>
            <p>
              Once your LinkedIn session token is transmitted to a partner
              platform, your data is governed by that platform's own privacy
              policy and terms of service. Profile Connector has no control
              over and assumes no responsibility for the privacy practices of
              partner platforms.
            </p>
            <p>
              We encourage you to review the privacy policy of the partner
              platform before connecting your LinkedIn account.
            </p>
          </section>

          <section className="tos-section" id="your-rights">
            <h2>10. Your Rights</h2>
            <p>
              Because Profile Connector does not store personally identifiable
              information, most data subject rights (access, rectification,
              erasure) do not apply to data held by us — there is no personal
              profile to access or delete.
            </p>
            <p>
              If you have questions about data held by a partner platform,
              please contact that platform directly using their privacy contact
              information.
            </p>
            <p>
              You may withdraw your consent to the use of this Service at any
              time by uninstalling the Profile Connector Chrome extension and
              disconnecting your account via the partner platform's dashboard.
            </p>
          </section>

          <section className="tos-section" id="children">
            <h2>11. Children's Privacy</h2>
            <p>
              The Service is not directed to individuals under the age of 18.
              We do not knowingly collect data from children. If you believe a
              child has used the Service, please contact us so we can
              investigate.
            </p>
          </section>

          <section className="tos-section" id="changes">
            <h2>12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we make
              material changes, we will update the effective date at the top of
              this page. We encourage you to review this policy periodically.
            </p>
            <p>
              Your continued use of the Service after changes to this policy
              constitutes your acceptance of the revised policy.
            </p>
          </section>

          <section className="tos-section" id="contact">
            <h2>13. Contact Us</h2>
            <p>
              If you have questions or concerns about this Privacy Policy or
              our data practices, please contact us:
            </p>
            <div className="tos-contact-card">
              <p>
                <strong>Profile Connector</strong>
              </p>
              <p>
                For privacy-related questions or data requests, contact us via
                the Chrome Web Store listing support link, or through the
                support page of the partner platform you are using.
              </p>
              <p>
                For security vulnerability reports, please describe the issue
                in detail so our team can respond appropriately. We aim to
                respond to all privacy inquiries within 30 days.
              </p>
            </div>

            <div className="tos-highlight" style={{ marginTop: "32px" }}>
              <p>
                By using Profile Connector, you confirm that you have read and
                understood this Privacy Policy.
              </p>
            </div>
          </section>
        </main>
      </div>

      <footer className="tos-footer">
        <p>
          © {new Date().getFullYear()} Profile Connector. All rights reserved.
          &nbsp;·&nbsp;{" "}
          <Link to="/landing">Home</Link>
          &nbsp;·&nbsp;{" "}
          <Link to="/tos">Terms of Service</Link>
          &nbsp;·&nbsp;{" "}
          <Link to="/privacy">Privacy Policy</Link>
        </p>
      </footer>
    </div>
  );
}
